import type { AppState } from '../app-type-slice-reducer';
import { APP_TYPES } from '../app-type-slice-reducer';

interface WithAppType {
  appType: AppState;
}

/**
 * @description
 * Selector used to determine if we're currently in the admin app. Useful for
 * components and utilities that are shared between apps.
 * @returns `true` if we're in the admin app, `false` otherwise.
 * @example
 * ```ts
 * import { useSelector } from 'react-redux';
 * import { appTypeSliceSelectors } from '@highlight-cp/redux-state';
 *
 * const isAdminApp = useSelector(appTypeSliceSelectors.isAdminApp);
 * ```
 */
export const isAdminApp = ({ appType: state }: WithAppType) => state.appType === APP_TYPES.ADMIN;

/**
 * @description
 * Selector used to determine if we're currently in the highlighter app. Useful for
 * components and utilities that are shared between apps.
 * @returns `true` if we're in the highlighter app, `false` otherwise.
 * @example
 * ```ts
 * import { useSelector } from 'react-redux';
 * import { appTypeSliceSelectors } from '@highlight-cp/redux-state';
 *
 * const isHighlighterApp = useSelector(appTypeSliceSelectors.isHighlighterApp);
 * ```
 */
export const isHighlighterApp = ({ appType: state }: WithAppType) =>
  state.appType === APP_TYPES.HIGHLIGHTER;

/**
 * @description
 * Selector used to determine if we're currently in the client app. Useful for
 * components and utilities that are shared between apps.
 * @returns `true` if we're in the client app, `false` otherwise.
 * @example
 * ```ts
 * import { useSelector } from 'react-redux';
 * import { appTypeSliceSelectors } from '@highlight-cp/redux-state';
 *
 * const isClientApp = useSelector(appTypeSliceSelectors.isClientApp);
 * ```
 */
export const isClientApp = ({ appType: state }: WithAppType) => state.appType === APP_TYPES.CLIENT;
